import { Card, CardSection, Button } from '@trafiflow/shared-ui'
import { AlertTriangle, X } from 'lucide-react'

interface CancelContractModalProps {
  isOpen: boolean
  contractName: string
  onClose: () => void
  onConfirm: () => void
  isLoading?: boolean
  error?: string | null
}

export default function CancelContractModal({
  isOpen,
  contractName,
  onClose,
  onConfirm,
  isLoading = false,
  error = null,
}: CancelContractModalProps) {
  if (!isOpen) return null

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={isLoading ? undefined : onClose}
      />
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardSection>
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-full">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-tf-text">Cancel Contract</h3>
              </div>
              <button
                onClick={onClose}
                disabled={isLoading}
                className="p-1 text-tf-text-muted hover:text-tf-text-muted rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="mb-6">
              <p className="text-sm text-tf-text-muted mb-4">
                Are you sure you want to cancel the contract for <span className="font-semibold text-tf-text">{contractName}</span>?
              </p>
              <p className="text-sm text-tf-text-muted">
                This action cannot be undone. The contract will be marked as cancelled and billing will stop.
              </p>
              {error && (
                <div className="mt-4 p-3 bg-tf-feedback-error-bg border border-red-200 rounded-md">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t border-tf-border">
              <Button 
                variant="secondary" 
                onClick={onClose} 
                fullWidth 
                disabled={isLoading}
              >
                Keep Contract
              </Button>
              <Button 
                variant="critical" 
                onClick={onConfirm} 
                fullWidth 
                disabled={isLoading}
              >
                {isLoading ? 'Cancelling...' : 'Cancel Contract'}
              </Button>
            </div>
          </CardSection>
        </Card>
      </div>
    </>
  )
}

